
import { useAuth } from "@/context/AuthContext";
import { useMovieContext } from "@/context";

/**
 * Debug utility to monitor authentication and movie loading state
 * Only available in development mode
 */
export const useDebugMonitor = () => {
  const { user, session, loading: authLoading } = useAuth();
  const { allMovies, isLoading: moviesLoading } = useMovieContext();

  const debugState = {
    auth: {
      hasUser: !!user,
      userId: user?.id || null,
      userEmail: user?.email || null,
      hasSession: !!session,
      authLoading,
      sessionToken: session?.access_token ? 'present' : 'missing'
    },
    movies: {
      movieCount: allMovies.length,
      moviesLoading,
      favoriteCount: allMovies.filter(m => m.favorite).length,
      planToWatchCount: allMovies.filter(m => m.planToWatch).length,
      sampleTitles: allMovies.slice(0, 3).map(m => m.title)
    },
    timestamp: new Date().toISOString()
  };

  // Only expose in development
  if (process.env.NODE_ENV === 'development') {
    (window as any).debugAppState = debugState;
  }

  return debugState;
};

/**
 * Function to manually trigger debugging output
 */
export const logDebugState = () => {
  if (process.env.NODE_ENV !== 'development') {
    console.log('Debug logging only available in development mode');
    return;
  }

  const debugState = (window as any).debugAppState;
  if (debugState) {
    console.group('🔍 App Debug State');
    console.log('Auth State:', debugState.auth);
    console.log('Movies State:', debugState.movies);
    console.log('Timestamp:', debugState.timestamp);
    console.groupEnd();
  } else {
    console.log('No debug state available - make sure useDebugMonitor is being called');
  }
};

/**
 * Function to check if data persistence is working
 */
export const testDataPersistence = async () => {
  if (process.env.NODE_ENV !== 'development') {
    console.log('Data persistence testing only available in development mode');
    return;
  }

  console.group('🧪 Testing Data Persistence');
  
  // Check localStorage for auth tokens
  const authKeys = Object.keys(localStorage).filter(key => 
    key.includes('supabase') || key.includes('auth') || key.includes('token')
  );
  
  console.log('LocalStorage auth keys:', authKeys);
  
  // Check if we can access Supabase
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { user }, error } = await supabase.auth.getUser();
    
    console.log('Supabase auth check:', {
      hasUser: !!user,
      error: error?.message || 'none',
      userId: user?.id || 'none'
    });
    
    if (user) {
      // Try to fetch movies
      const { data: movies, error: moviesError } = await supabase
        .from('user_movies')
        .select('tmdb_id, title, favorite, plan_to_watch')
        .eq('user_id', user.id)
        .limit(5);
      
      console.log('Database query test:', {
        movieCount: movies?.length || 0,
        error: moviesError?.message || 'none',
        sampleMovies: movies?.map(m => ({
          title: m.title,
          favorite: m.favorite,
          planToWatch: m.plan_to_watch
        })) || []
      });
    }
  } catch (error) {
    console.error('Error testing persistence:', error);
  }
  
  console.groupEnd();
};

// Expose helper functions globally in development
if (process.env.NODE_ENV === 'development') {
  (window as any).logDebugState = logDebugState;
  (window as any).testDataPersistence = testDataPersistence;
  
  console.log('🔍 Debug helpers available:');
  console.log('  - window.logDebugState() - Log current app state');
  console.log('  - window.testDataPersistence() - Test data loading');
  console.log('  - window.debugAppState - Current state object');
}
